// Field + preset config for the Approvals FilterBuilder.
//
// Only these columns are exposed. Deliberately absent:
//   • `id`          — reserved for the keyset cursor; a client condition on it
//                     would collide with pagination.
//   • `snapshot`, `fingerprint`, `blocking_fingerprint` — internal columns.
//
// The old bespoke params are gone; each is now an ordinary `where` clause:
//   ?status=pending  →  { status: 'pending' }
//   ?action=x:y      →  { action: 'x:y' }
//   ?mine=true       →  { maker_id: <current user id> }

export const APPROVAL_STATUSES = [
  'pending',
  'approved',
  'denied',
  'executed',
  'failed',
  'expired',
];

// Relative-date literals the API substitutes server-side (sqlize-query), so the
// window is anchored to the server clock rather than the operator's browser.
// Sent verbatim as the condition value.
export const DATE_PRESETS = [
  { value: '@NOW', label: 'Now' },
  { value: '@LAST_10_MINUTES', label: 'Last 10 minutes' },
  { value: '@LAST_24_HOURS', label: 'Last 24 hours' },
  { value: '@TODAY', label: 'Today' },
  { value: '@YESTERDAY', label: 'Yesterday' },
  { value: '@THIS_WEEK', label: 'This week' },
  { value: '@LAST_WEEK', label: 'Last week' },
  { value: '@THIS_MONTH', label: 'This month' },
  { value: '@LAST_MONTH', label: 'Last month' },
];

// Static half of the config. The `action` field is appended by
// approvalFields() below because its vocabulary is fetched, not hardcoded.
const STATIC_FIELDS = [
  { key: 'status', label: 'Status', type: 'enum', options: APPROVAL_STATUSES },
  { key: 'title', label: 'Title', type: 'string' },
  { key: 'reference', label: 'Reference', type: 'string' },
  { key: 'maker_id', label: 'Maker ID', type: 'number' },
  { key: 'otp_required', label: 'OTP Required', type: 'boolean' },
  { key: 'created_at', label: 'Created', type: 'date', presets: DATE_PRESETS },
  { key: 'expires_at', label: 'Expires', type: 'date', presets: DATE_PRESETS },
];

/**
 * Full field list for the FilterBuilder.
 *
 * The action vocabulary grows every time an operation is put behind
 * maker-checker, so it is fetched from /approval-request-actions rather than
 * listed here. Until it arrives the Action field is offered with no options —
 * FilterBuilder then falls back to a free-text input, so the filter still works
 * for someone who knows the exact action key.
 *
 * @param {Array} actionOptions — [{ value, label }] from approvals.listActions()
 */
export function approvalFields(actionOptions) {
  return STATIC_FIELDS.concat([
    {
      key: 'action',
      label: 'Action',
      type: 'enum',
      options: actionOptions || [],
    },
  ]);
}

// Column allow-list for the serializer — never contains `id` or the internal
// columns. Independent of the fetched actions, which only affect `action`'s
// values, not which columns are filterable.
export const ALLOWED_FIELDS = STATIC_FIELDS.map((f) => f.key).concat('action');
